import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpParams
} from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import { CookieService } from 'ngx-cookie-service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Constants } from 'app/shared/config/constants';
import { AuthResponse } from 'app/shared/services/auth.service';
import { ConfigurationService } from 'app/shared/services/configuration.service';

@Injectable ( {
  providedIn: 'root',
} )
export class AiTokenService {

  constructor (
    private http: HttpClient,
    private cookieService: CookieService,
    private configService: ConfigurationService,
    private jwtHelper: JwtHelperService,
  ) {
  }

  retrieve ( incidentId: string ): Observable<string> {
    const params = new HttpParams ().set ( 'incidentId', incidentId );
    return this.http.post<AuthResponse> ( Constants.aiTokenUrl, null, { params } )
      .pipe (
        map ( response => {
          if ( !response?.accessToken?.trim () ) {
            throw new Error ( 'Invalid AI token response' );
          }
          return response.accessToken;
        } ),
      );
  }

  storeCookie ( token: string ): void {
    // Another tab may have logged the user out while the token was being retrieved
    if ( !this.jwtHelper.tokenGetter () ) {
      throw new Error ( 'The user is no longer logged in' );
    }
    this.cookieService.set ( Constants.aiTokenCookieName, token, {
      expires: this.jwtHelper.getTokenExpirationDate ( token ) ?? undefined,
      path: Constants.aiTokenCookiePath,
      domain: this.cookieDomain,
      secure: this.isSecure,
      sameSite: 'Strict',
    } );
    if ( this.cookieService.get ( Constants.aiTokenCookieName ) !== token ) {
      throw new Error ( 'The AI token cookie was rejected by the browser' );
    }
  }

  deleteCookie ( ownedToken?: string ): void {
    // Another tab may have replaced the cookie with its own token
    if ( ownedToken && this.cookieService.get ( Constants.aiTokenCookieName ) !== ownedToken ) {
      return;
    }
    this.cookieService.delete (
      Constants.aiTokenCookieName,
      Constants.aiTokenCookiePath,
      this.cookieDomain,
      this.isSecure,
      'Strict',
    );
  }

  private get cookieDomain (): string | undefined {
    const host = this.currentHost;
    const domain = ( ( this.configService.byKey ( 'cookie.domain' ) as string ) ?? '' )
      .split ( ',' )
      .map ( d => d.trim().replace ( /^\./, '' ) )
      .find ( d => !!d && ( host === d || host.endsWith ( `.${ d }` ) ) );
    // The browser rejects a cookie for a domain the page is not under (for example on localhost).
    // A leading dot is set explicitly so the cookie is shared with every subdomain (e.g. chat.<domain>)
    // rather than being host-only.
    return domain ? `.${ domain }` : undefined;
  }

  private get currentHost (): string {
    return location.hostname;
  }

  private get isSecure (): boolean {
    return location.protocol === 'https:';
  }
}
